import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, LogOut, FileText, CheckCircle2, FileEdit, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, categoryLabel, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AshokaChakra } from "@/components/AshokaChakra";
import { NarratorSettings } from "@/components/NarratorSettings";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const [subs, setSubs] = useState(null);

  const load = () =>
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch(() => setSubs({ confirmed: 0, pending: 0 }));
  }, []);

  const remove = async () => {
    try {
      await api.delete(`/admin/articles/${toDelete.id}`);
      toast.success("Article deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const published = articles.filter((a) => a.published).length;

  return (
    <div className="relative z-10 min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-[var(--line)] tricolor-light-h backdrop-blur">
        <div className="tricolor-bar h-1" />
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-3">
            <AshokaChakra className="h-8 w-8 text-[var(--chakra)]" />
            <span className="font-devanagari text-2xl text-[var(--chakra-deep)]">भारत गाथा</span>
            <span className="ml-2 rounded-full bg-[var(--saffron-tint)] px-2.5 py-0.5 text-xs font-semibold text-[var(--saffron-deep)]">Author desk</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-stone-500 sm:block" data-testid="admin-user-email">{user?.email}</span>
            <Link to="/" data-testid="admin-view-site-link" className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] bg-white px-3 py-1.5 text-sm font-medium hover:border-[var(--saffron)]"><Eye className="h-4 w-4" /> Site</Link>
            <button onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-logout-btn" className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] bg-white px-3 py-1.5 text-sm font-medium hover:border-red-300 hover:text-red-600"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow text-[var(--saffron-deep)]">नमस्ते, लेखक</p>
            <h1 className="mt-2 font-serif text-3xl font-bold text-[var(--chakra-deep)] sm:text-4xl">Your articles</h1>
          </div>
          <Link to="/admin/new" data-testid="admin-new-article-btn" className="inline-flex items-center gap-2 rounded-full bg-[var(--saffron)] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition-all hover:bg-[var(--saffron-deep)]">
            <Plus className="h-4 w-4" /> New article
          </Link>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: FileText, label: "Total", value: articles.length, tone: "text-[var(--chakra)]" },
            { icon: CheckCircle2, label: "Published", value: published, tone: "text-[var(--india-green)]" },
            { icon: FileEdit, label: "Drafts", value: articles.length - published, tone: "text-[var(--saffron-deep)]" },
            { icon: Mail, label: "Subscribers", value: subs ? subs.confirmed : "…", tone: "text-[var(--chakra)]", hint: subs?.pending ? `${subs.pending} pending confirmation` : "Emailed on every publish" },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-4 rounded-2xl border border-[var(--line)] bg-white p-5" data-testid={`admin-stat-${s.label.toLowerCase()}`}>
              <s.icon className={`h-8 w-8 ${s.tone}`} />
              <div>
                <p className="font-serif text-3xl font-bold text-stone-900">{s.value}</p>
                <p className="text-xs uppercase tracking-wider text-stone-500">{s.label}</p>
                {s.hint && <p className="text-[11px] text-stone-400">{s.hint}</p>}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8"><NarratorSettings /></div>

        <div className="mt-8 overflow-hidden rounded-2xl border border-[var(--line)] bg-white" data-testid="admin-articles-list">
          {loading ? (
            <div className="p-10 text-center text-stone-500">Loading...</div>
          ) : articles.length === 0 ? (
            <div className="p-12 text-center text-stone-500" data-testid="admin-articles-empty">No articles yet. Write your first one.</div>
          ) : (
            <ul className="divide-y divide-[var(--line)]">
              {articles.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center gap-4 p-5 transition-colors hover:bg-[var(--paper)]" data-testid={`admin-article-row-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${a.published ? "bg-[var(--green-tint)] text-[var(--india-green-deep)]" : "bg-[var(--saffron-tint)] text-[var(--saffron-deep)]"}`} data-testid={`admin-article-status-${a.id}`}>
                        {a.published ? "Published" : "Draft"}
                      </span>
                      <span className="text-xs text-stone-500">{categoryLabel(a.category)}</span>
                      {a.content_hi && <span className="rounded bg-[var(--saffron-tint)] px-1.5 py-0.5 font-devanagari text-[11px] text-[var(--saffron-deep)]" data-testid={`admin-article-hindi-${a.id}`}>हिन्दी ✓</span>}
                    </div>
                    <p className="mt-1 truncate font-serif text-lg font-semibold text-[var(--chakra-deep)]">{a.title}</p>
                    <p className="text-xs text-stone-500">Updated {formatDate(a.updated_at)} · {a.read_time} min read{a.notified_count != null && ` · emailed to ${a.notified_count} subscriber${a.notified_count === 1 ? "" : "s"}`}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {a.published && (
                      <Link to={`/articles/${a.slug}`} target="_blank" data-testid={`admin-article-view-${a.id}`} className="rounded-lg p-2 text-stone-500 hover:bg-[var(--chakra-tint)] hover:text-[var(--chakra)]" title="View"><Eye className="h-4 w-4" /></Link>
                    )}
                    <Link to={`/admin/edit/${a.id}`} data-testid={`admin-article-edit-${a.id}`} className="rounded-lg p-2 text-stone-500 hover:bg-[var(--saffron-tint)] hover:text-[var(--saffron-deep)]" title="Edit"><Pencil className="h-4 w-4" /></Link>
                    <button onClick={() => setToDelete(a)} data-testid={`admin-article-delete-${a.id}`} className="rounded-lg p-2 text-stone-500 hover:bg-red-50 hover:text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this article?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.title}" will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="delete-confirm-btn" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
